import { useState, useCallback } from 'react';

import { getAuthorised } from 'approot/shared/api/auth/auth.http';
import { isResponseHeadOk } from 'lib/network/http';
import { downloadDoc } from 'approot/shared/download-doc/download-doc';
import { WORD_DOCUMENT_TYPE } from 'lib/form/file-dropper/file-dropper.constants';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';

export const WORD = 'word';
export const PDF = 'pdf';
export type ExportFormat = typeof WORD | typeof PDF;

const useExportProgram = ({
  unitId,
  unitName,
  onError,
  onSuccess,
}: {
  unitId: number;
  unitName: string;
  onError?: () => void;
  onSuccess?: () => void;
}) => {
  const [isLoading, setIsloading] = useState<boolean>(false);

  const currentClass = useCurrentClassVar();
  const classId = currentClass?.id;

  const handleExportProgram = useCallback(
    async (options: {
      format?: ExportFormat;
      includeDraftActivities?: boolean;
    }) => {
      if (isLoading) return;

      setIsloading(true);

      const response = await getAuthorised({
        path: `/api/export-program/unit`,
        pathParam: unitId.toString(),
        getFields: {
          userClassId: classId,
          format: options.format ? options.format : WORD,
          includeDraftActivities: options.includeDraftActivities,
        },
        headerFields: {
          accept: WORD_DOCUMENT_TYPE,
        },
      });

      if (!isResponseHeadOk(response)) {
        setIsloading(false);
        onError?.();
      } else if (options.format === PDF) {
        onSuccess?.();
        return;
      }

      const file = response && response.blob && (await response.blob());
      const fileType = response?.headers.get('Content-Type');

      if (!file || !file.size || !fileType?.endsWith('document')) {
        setIsloading(false);
        return;
      }

      setIsloading(false);

      downloadDoc({
        fileName: unitName || 'program',
        docFile: file,
        docUrl: URL.createObjectURL(file),
        extension: 'docx',
      });
      onSuccess?.();
    },
    [unitId, unitName, isLoading, classId, onError, onSuccess]
  );

  return {
    isLoading,
    handleExportProgram,
  };
};

export default useExportProgram;
